import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  listApprovals,
  reviewApproval,
  approveApproval,
  requestRevisionApproval,
  publishApproval,
  type ApprovalItem,
  type ReviewData,
} from "../../../service/approvals"

const Approvals = () => {
  const [items, setItems] = useState<ApprovalItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await listApprovals()
      setItems(r?.items || [])
    } catch (e: any) {
      // Show the reason inline rather than a toast (avoids the dev StrictMode
      // double-toast, and a 403 role-gate is expected for non-admin users).
      setError(e?.response?.data?.message || "Failed to load approvals")
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Approvals</h3>
            <p className="text-muted mb-0">
              Result breakdowns awaiting approval. Click a row to review per-subject completion
              before approving.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : error ? (
          <div className="card">
            <div className="card-body text-center text-danger py-5">{error}</div>
          </div>
        ) : items.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted py-5">Nothing waiting for review.</div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {items.map((bd) => (
              <div key={bd.id} className="card mb-0">
                <button
                  type="button"
                  onClick={() => setOpenId(openId === bd.id ? null : bd.id)}
                  className="btn text-start w-100 d-flex align-items-center justify-content-between p-3"
                >
                  <div>
                    <div className="fw-semibold">{bd.title}</div>
                    <div className="text-muted small">
                      {bd.session_name} • {bd.campus_name} • {bd.exam_term_name}
                    </div>
                  </div>
                  <span
                    className={`badge ${
                      bd.status === "submitted" ? "bg-warning text-dark" : "bg-danger"
                    }`}
                  >
                    {bd.status}
                  </span>
                </button>
                {openId === bd.id && <ReviewPanel id={bd.id} onDone={load} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const ReviewPanel = ({ id, onDone }: { id: string; onDone: () => void }) => {
  const [data, setData] = useState<ReviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const loadReview = async () => {
    setLoading(true)
    try {
      const r = await reviewApproval(id)
      setData(r)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load review")
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const act = async (fn: () => Promise<any>) => {
    setBusy(true)
    try {
      await fn()
      toast.success("Done")
      onDone()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Action failed")
    } finally {
      setBusy(false)
    }
  }

  if (loading || !data) {
    return <div className="border-top px-3 py-3 text-muted small">Loading review…</div>
  }

  const { subjects, all_ready, recent_actions } = data

  return (
    <div className="border-top px-3 py-3">
      <div className="table-responsive">
        <table className="table table-bordered table-sm mb-0">
          <thead className="table-light">
            <tr>
              <th>Class</th>
              <th>Subject</th>
              <th className="text-end">Students</th>
              <th className="text-end">Marks entered</th>
              <th className="text-end">Final calcs</th>
              <th>Ready</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((s) => (
              <tr key={s.course_structure_id}>
                <td>{s.class_name}</td>
                <td>{s.subject_name}</td>
                <td className="text-end">{s.student_count}</td>
                <td className={`text-end ${s.entered_marks < s.expected_marks ? "text-warning" : ""}`}>
                  {s.entered_marks} / {s.expected_marks}
                </td>
                <td className="text-end">
                  {s.is_carryover_final ? `${s.final_calculations} / ${s.student_count}` : "—"}
                </td>
                <td>{s.ready ? <span className="text-success">✓</span> : <span className="text-muted">—</span>}</td>
              </tr>
            ))}
            {subjects.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  No subjects configured for this breakdown.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
        <button
          className="btn btn-primary btn-sm"
          disabled={busy || !all_ready || data.breakdown.status === "approved" || data.breakdown.status === "published"}
          onClick={() => act(() => approveApproval(id))}
        >
          Approve
        </button>
        <button
          className="btn btn-outline-danger btn-sm"
          disabled={busy}
          onClick={() => {
            const notes = window.prompt("Reason for sending back to teachers:")
            if (notes === null) return
            act(() => requestRevisionApproval(id, notes))
          }}
        >
          Request revision
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          disabled={busy || data.breakdown.status !== "approved"}
          onClick={() => act(() => publishApproval(id))}
        >
          Publish
        </button>
        {!all_ready && (
          <span className="text-warning small">
            Some subjects are incomplete — approval is disabled.
          </span>
        )}
      </div>

      {recent_actions?.length > 0 && (
        <div className="mt-3">
          <h6 className="fw-semibold mb-2">Recent actions</h6>
          <ul className="list-unstyled mb-0 small text-muted">
            {recent_actions.map((a) => (
              <li key={a.id}>
                <span className="fw-semibold text-capitalize">{a.action.replace("_", " ")}</span> by{" "}
                {a.actor_name || "—"} at {new Date(a.actioned_at).toLocaleString()}
                {a.notes && <span> — {a.notes}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default Approvals
